import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import GameCard from './components/GameCard';
import ArticleCard from './components/ArticleCard';
import FAQSection from './components/FAQSection';
import { categories, games, articles, faqs } from '@/mocks/data';

const hotGames = games.filter((g) => g.isHot);
const latestArticles = articles.slice(0, 4);

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'PG官网',
  url: 'https://www.pg-official.com/',
  description: 'PG官网是玩家获取正版游戏资源、账号注册与安全登录的权威渠道。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.pg-official.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PG官网',
  url: 'https://www.pg-official.com/',
  description: 'PG官方正版游戏平台',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-XXXX',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  return (
    <>
      <SEOHead
        title="PG官网 - 官方正版入口与完整指南 | PG游戏官方平台"
        description="PG官网是玩家获取正版游戏资源、账号注册与安全登录的权威渠道。提供PG官网登录入口、PG官网注册指引、PG官网游戏大全、最新地址及官方下载，涵盖丰富游戏种类与优惠活动，享受安全可靠的游戏体验。"
        canonicalUrl="https://www.pg-official.com/"
        schema={{ ...websiteSchema, ...organizationSchema }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Epic%20fantasy%20gaming%20world%20landscape%20with%20glowing%20crystals%20and%20floating%20islands%2C%20warm%20golden%20and%20amber%20toned%20atmosphere%2C%20cinematic%20game%20background%20art%2C%20mystical%20energy%20flows%2C%20dramatic%20clouds%2C%20adventure%20vibe&width=1920&height=900&seq=hero-pg-bg&orientation=landscape"
            alt="PG官网 - 官方正版游戏平台"
            title="PG官网首页"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

          <div className="relative z-10 text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-heading leading-tight">
              <a href="/" className="text-white">PG官网 - 官方正版入口</a>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              PG官网是玩家获取正版游戏资源、账号注册与安全登录的权威渠道，为您提供<strong>PG官网登录入口</strong>、<strong>PG官网注册</strong>指引与<strong>PG官网游戏大全</strong>一站式服务
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                立即注册
              </Link>
              <Link
                to="/article/pg-guanwang-xinshou-zhinan"
                className="px-8 py-3 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-full font-medium text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap"
              >
                新手指南
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {[
                { icon: 'ri-user-add-line', value: '500万+', label: '注册玩家' },
                { icon: 'ri-gamepad-line', value: '200+', label: '精品游戏' },
                { icon: 'ri-shield-check-line', value: '99.9%', label: '安全稳定' },
                { icon: 'ri-customer-service-2-line', value: '7×24h', label: '客服支持' },
              ].map((stat, i) => (
                <div key={i} className="flex flex-col items-center text-center p-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className={`${stat.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <span className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{stat.value}</span>
                  <span className="text-sm text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                <Link to="/category/dongzuo" className="text-foreground-900">PG官网游戏大全</Link>
              </h2>
              <p className="text-foreground-500">丰富游戏类型，总有一款适合你</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors duration-300 mb-3">
                    <i className={`${cat.icon} text-accent-600 text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900 font-heading mb-2 whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-500 text-center leading-relaxed hidden sm:block">
                    {cat.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Games Section */}
        <section className="py-16 px-4 md:px-6" data-product-shop>
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                  热门游戏推荐
                </h2>
                <p className="text-foreground-500 text-sm">千万玩家的共同选择</p>
              </div>
              <Link
                to="/category/dongzuo"
                className="flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {hotGames.map((game) => (
                <GameCard
                  key={game.id}
                  slug={game.slug}
                  name={game.name}
                  image={game.image}
                  category={categories.find((c) => c.slug === game.category)?.name || ''}
                  rating={game.rating}
                  players={game.players}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Promotions Section */}
        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                最新优惠活动
              </h2>
              <p className="text-foreground-500">PG官网为您准备了丰富的福利，登录即享</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-gift-line',
                  title: '新玩家首充礼包',
                  desc: '首次充值任意金额，额外赠送50%游戏币和限定道具',
                  color: 'bg-accent-100',
                  iconColor: 'text-accent-600',
                },
                {
                  icon: 'ri-vip-crown-line',
                  title: 'VIP会员特权',
                  desc: '开通VIP享受每日签到奖励翻倍、专属客服通道',
                  color: 'bg-primary-100',
                  iconColor: 'text-primary-600',
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '老玩家回归奖励',
                  desc: '回归玩家登录即送海量游戏币和稀有皮肤礼包',
                  color: 'bg-secondary-100',
                  iconColor: 'text-secondary-600',
                },
              ].map((promo, i) => (
                <div
                  key={i}
                  className="p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full ${promo.color} mb-4`}>
                    <i className={`${promo.icon} ${promo.iconColor} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2 font-heading">{promo.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{promo.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                  最新资讯
                </h2>
                <p className="text-foreground-500 text-sm">PG官网最新动态与游戏攻略</p>
              </div>
              <Link
                to="/article/pg-guanwang-xinshou-zhinan"
                className="flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                更多文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-100">
          <FAQSection faqs={faqs} />
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gaming%20energy%20flow%20with%20soft%20golden%20particles%20and%20light%20beams%2C%20dark%20background%20with%20subtle%20texture%2C%20modern%20tech%20atmosphere%2C%20elegant%20style&width=1920&height=600&seq=cta-pg-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60"></div>
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-4">
              准备好开启你的游戏之旅了吗？
            </h2>
            <p className="text-white/70 mb-8">
              立即加入PG官网，数百万玩家正在等待你的到来
            </p>
            <Link
              to="/about"
              className="inline-flex px-8 py-3 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-all duration-300 whitespace-nowrap"
            >
              立即注册
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}